import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "../styles/Home.css";

// Import images
import heroBoardEn from "../assets/hero-board-182-en.jpg";
import heroBoardPl from "../assets/hero-board-182-pl.jpg";
import heroBoardDe from "../assets/hero-board-182-de.jpg";
import heroBoardEs from "../assets/hero-board-182-es.jpg";
import heroBoardFr from "../assets/hero-board-182-fr.jpg";
import heroBoardIt from "../assets/hero-board-182-it.jpg";
import heroBoardUk from "../assets/hero-board-182-uk.jpg";
import featureRoleplay from "../assets/feature-roleplay-182.jpg";
import featureFlashcards from "../assets/feature-flashcards-182.jpg";
import featureTeacher from "../assets/feature-teacher.jpeg";
import featureMusic from "../assets/feature-music-182.jpg";
import featureMissions from "../assets/feature-missions-182.jpg";

// Inline SVG icons (Lucide-style stroked)
const Icon = ({ children, size = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const IconMessageCircle = (p) => (
  <Icon {...p}>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </Icon>
);
const IconUsers = (p) => (
  <Icon {...p}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Icon>
);
const IconLayers = (p) => (
  <Icon {...p}>
    <polygon points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </Icon>
);
const IconGraduationCap = (p) => (
  <Icon {...p}>
    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
    <path d="M6 12v5c3 3 9 3 12 0v-5" />
  </Icon>
);
const IconRepeat = (p) => (
  <Icon {...p}>
    <polyline points="17 1 21 5 17 9" />
    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <polyline points="7 23 3 19 7 15" />
    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </Icon>
);
const IconSparkles = (p) => (
  <Icon {...p}>
    <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5z" />
    <path d="M19 14l.8 2.4L22 17l-2.2.6L19 20l-.8-2.4L16 17l2.2-.6z" />
  </Icon>
);
const IconCheck = (p) => (
  <Icon {...p}>
    <polyline points="20 6 9 17 4 12" />
  </Icon>
);
const IconChevronDown = (p) => (
  <Icon {...p}>
    <polyline points="6 9 12 15 18 9" />
  </Icon>
);
const IconActivity = (p) => (
  <Icon {...p}>
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </Icon>
);
const IconMail = (p) => (
  <Icon {...p}>
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </Icon>
);
const IconMusic = (p) => (
  <Icon {...p}>
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </Icon>
);
const IconMapPin = (p) => (
  <Icon {...p}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </Icon>
);
const IconBriefcase = (p) => (
  <Icon {...p}>
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </Icon>
);

// Platform store badges
const AppStoreBadge = () => (
  <svg width="20" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);
const PlayStoreBadge = () => (
  <svg width="20" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zM14.892 13.1l2.302 2.302-12.41 7.105 10.108-9.407zM18.288 8.61l2.832 1.622a1 1 0 0 1 0 1.736l-2.832 1.622-2.49-2.49 2.49-2.49zM4.784 1.407l12.41 7.105-2.302 2.302L4.784 1.407z" />
  </svg>
);

function StatValue({ value, animate }) {
  const [display, setDisplay] = useState(value);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!animate) { setDisplay(value); return; }
    // Extract leading number and suffix (e.g. "14-day" → 14, "-day")
    const match = value.match(/^(\d+)(.*)/);
    if (!match) { setDisplay(value); return; }
    const target = parseInt(match[1], 10);
    const suffix = match[2];
    const duration = 1200;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * target) + suffix);
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [animate, value]);

  return <>{display}</>;
}

const heroBoards = {
  en: heroBoardEn,
  pl: heroBoardPl,
  de: heroBoardDe,
  es: heroBoardEs,
  fr: heroBoardFr,
  it: heroBoardIt,
  uk: heroBoardUk,
};

function Home() {
  const observerRef = useRef(null);
  const phoneRef = useRef(null);
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const { t, i18n } = useTranslation();
  const heroBoard = heroBoards[(i18n.language || "en").split("-")[0]] || heroBoardEn;

  // Scroll-driven hero phone tilt
  useEffect(() => {
    const handleScroll = () => {
      if (!phoneRef.current) return;
      const scrollY = window.scrollY;
      const maxTilt = 8;
      // Map scroll 0-600px to tilt 0-maxTilt degrees
      const progress = Math.min(scrollY / 600, 1);
      const tilt = progress * maxTilt;
      phoneRef.current.style.transform = `rotateY(${-tilt}deg) rotateX(${tilt * 0.3}deg)`;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Stat counter animation
  useEffect(() => {
    const statsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setStatsVisible(true);
            statsObserver.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) statsObserver.observe(statsRef.current);
    return () => statsObserver.disconnect();
  }, []);

  useEffect(() => {
    // Intersection Observer for scroll animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const animatedElements = document.querySelectorAll(".animate-on-scroll");
    animatedElements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const languages = [
    { flag: "🇵🇹", nameKey: "languages.portuguese" },
    { flag: "🇳🇴", nameKey: "languages.norwegian" },
    { flag: "🇬🇧", nameKey: "languages.english" },
    { flag: "🇪🇸", nameKey: "languages.spanish" },
    { flag: "🇩🇪", nameKey: "languages.german" },
    { flag: "🇮🇹", nameKey: "languages.italian" },
    { flag: "🇫🇷", nameKey: "languages.french" },
  ];

  const features = [
    {
      id: "roleplay",
      Icon: IconUsers,
      title: t("features.roleplay.title"),
      shortDesc: t("features.roleplay.shortDesc"),
      fullDesc: t("features.roleplay.fullDesc"),
      highlight: t("features.roleplay.highlight"),
      image: featureRoleplay,
      imageAlt: "Role-Playing Scenarios Feature",
    },
    {
      id: "flashcards",
      Icon: IconLayers,
      title: t("features.flashcards.title"),
      shortDesc: t("features.flashcards.shortDesc"),
      fullDesc: t("features.flashcards.fullDesc"),
      highlight: t("features.flashcards.highlight"),
      image: featureFlashcards,
      imageAlt: "Flashcard Challenges Feature",
    },
    {
      id: "teacher",
      Icon: IconGraduationCap,
      title: t("features.teacher.title"),
      shortDesc: t("features.teacher.shortDesc"),
      fullDesc: t("features.teacher.fullDesc"),
      highlight: t("features.teacher.highlight"),
      image: featureTeacher,
      imageAlt: "Teacher Method Feature",
    },
    {
      id: "missions",
      Icon: IconMapPin,
      title: t("features.missions.title"),
      shortDesc: t("features.missions.shortDesc"),
      fullDesc: t("features.missions.fullDesc"),
      highlight: t("features.missions.highlight"),
      image: featureMissions,
      imageAlt: "Trip Missions Feature",
    },
    {
      id: "music",
      Icon: IconMusic,
      title: t("features.music.title"),
      shortDesc: t("features.music.shortDesc"),
      fullDesc: t("features.music.fullDesc"),
      highlight: t("features.music.highlight"),
      image: featureMusic,
      imageAlt: "Music Discovery Feature",
    },
  ];

  const methodology = [
    {
      Icon: IconRepeat,
      titleKey: "methodology.cards.spacedRepetition.title",
      descKey: "methodology.cards.spacedRepetition.desc",
    },
    {
      Icon: IconMessageCircle,
      titleKey: "methodology.cards.immersive.title",
      descKey: "methodology.cards.immersive.desc",
    },
    {
      Icon: IconActivity,
      titleKey: "methodology.cards.adaptive.title",
      descKey: "methodology.cards.adaptive.desc",
    },
  ];

  const stats = [
    { value: t("stats.languages.value"), label: t("stats.languages.label") },
    { value: t("stats.method.value"), label: t("stats.method.label") },
    { value: t("stats.platforms.value"), label: t("stats.platforms.label") },
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero-section" id="home">
        <div className="hero-background">
          <div className="hero-gradient-orb hero-orb-1"></div>
          <div className="hero-gradient-orb hero-orb-2"></div>
        </div>
        
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              {t("hero.title")}
              <span className="hero-title-highlight">{t("hero.titleHighlight")}</span>
            </h1>
            <p className="hero-subtitle">
              {t("hero.subtitle")}
            </p>
            
            <div className="hero-cta-group">
              <a
                href="https://apps.apple.com/pl/app/dialecto/id6746904195"
                target="_blank"
                rel="noopener noreferrer"
                className="store-button apple"
                aria-label={t("hero.downloadAppStore")}
              >
                <AppStoreBadge />
                <span className="store-button-text">
                  <span className="store-button-eyebrow">{t("hero.downloadOn")}</span>
                  <span className="store-button-name">App Store</span>
                </span>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.dialectoapp&hl=en"
                target="_blank"
                rel="noopener noreferrer"
                className="store-button google"
                aria-label={t("hero.downloadPlayStore")}
              >
                <PlayStoreBadge />
                <span className="store-button-text">
                  <span className="store-button-eyebrow">{t("hero.getItOn")}</span>
                  <span className="store-button-name">Google Play</span>
                </span>
              </a>
            </div>

            <div className="hero-languages">
              <p className="languages-label">{t("hero.availableLanguages")}</p>
              <div className="language-flags">
                {languages.map((lang, index) => (
                  <div key={index} className="language-flag" title={t(lang.nameKey)}>
                    <span className="flag-emoji">{lang.flag}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-board" ref={phoneRef}>
              <img
                src={heroBoard}
                alt="Dialecto — make words stick with AI mnemonics"
                className="hero-board-img"
              />
              <div className="phone-glow"></div>
            </div>
            <div className="hero-phone-caption">
              <IconSparkles size={16} />
              <span>{t("hero.phoneCaption")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="stats-strip" aria-label="Highlights" ref={statsRef}>
        <div className="stats-strip-inner">
          {stats.map((s, i) => (
            <div key={i} className="stat-item">
              <div className="stat-value">
                <StatValue value={s.value} animate={statsVisible} />
              </div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Overview */}
      <section className="features-overview" id="features">
        <div className="section-header animate-on-scroll">
          <span className="section-badge">{t("features.sectionBadge")}</span>
          <h2 className="section-title">{t("features.sectionTitle")}</h2>
          <p className="section-subtitle">
            {t("features.sectionSubtitle")}
          </p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className="feature-card animate-on-scroll"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="feature-icon">
                <feature.Icon size={22} />
              </div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-desc">{feature.shortDesc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Feature Showcase (tabbed) */}
      <FeatureShowcase features={features} eyebrowLabel={t("features.eyebrow")} />

      {/* Dark editorial section */}
      <section className="dark-section">
        <div className="dark-section-inner">
          <span className="dark-eyebrow">{t("darkSection.eyebrow")}</span>
          <h2 className="dark-headline">{t("darkSection.headline")}</h2>
          <p className="dark-subline">{t("darkSection.subline")}</p>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section animate-on-scroll" id="pricing">
        <div className="section-header">
          <span className="section-badge">{t("pricing.sectionBadge")}</span>
          <h2 className="section-title">{t("pricing.sectionTitle")}</h2>
          <p className="section-subtitle">
            {t("pricing.sectionSubtitle")}
          </p>
        </div>

        <div className="pricing-cards">
          {/* Premium Plan */}
          <div className="pricing-card premium">
            <div className="popular-badge">{t("pricing.premium.badge")}</div>
            <div className="pricing-card-header">
              <h3 className="plan-name">{t("pricing.premium.name")}</h3>
              <div className="plan-price">
                <span className="price-original">{t("pricing.premium.priceOriginal")}</span>
                <span className="price-amount">{t("pricing.premium.price")}</span>
                <span className="price-period">{t("pricing.premium.period")}</span>
              </div>
              <p className="plan-desc">{t("pricing.premium.desc")}</p>
            </div>
            <ul className="plan-features">
              <li className="feature-included">
                <span className="feature-icon"><IconCheck size={14} /></span>
                <strong>{t("pricing.premium.features.trial")}</strong>
              </li>
              <li className="feature-included">
                <span className="feature-icon"><IconCheck size={14} /></span>
                {t("pricing.premium.features.advancedAI")}
              </li>
              <li className="feature-included">
                <span className="feature-icon"><IconCheck size={14} /></span>
                {t("pricing.premium.features.unlimited")}
              </li>
              <li className="feature-included">
                <span className="feature-icon"><IconCheck size={14} /></span>
                {t("pricing.premium.features.allModes")}
              </li>
              <li className="feature-included">
                <span className="feature-icon"><IconCheck size={14} /></span>
                {t("pricing.premium.features.adFree")}
              </li>
              <li className="feature-included">
                <span className="feature-icon"><IconCheck size={14} /></span>
                {t("pricing.premium.features.support")}
              </li>
            </ul>
            <div className="pricing-cta-group">
              <a
                href="https://apps.apple.com/pl/app/dialecto/id6746904195"
                target="_blank"
                rel="noopener noreferrer"
                className="pricing-cta primary"
                aria-label={t("hero.downloadAppStore")}
              >
                <AppStoreBadge /> App Store
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.dialectoapp&hl=en"
                target="_blank"
                rel="noopener noreferrer"
                className="pricing-cta primary"
                aria-label={t("hero.downloadPlayStore")}
              >
                <PlayStoreBadge /> Google Play
              </a>
            </div>
          </div>
        </div>

        <p className="pricing-note">
          {t("pricing.note")}
        </p>
      </section>

      {/* Methodology Section (formerly Technology) */}
      <section className="technology-section animate-on-scroll" id="methodology">
        <div className="section-header">
          <span className="section-badge">{t("methodology.sectionBadge")}</span>
          <h2 className="section-title">{t("methodology.sectionTitle")}</h2>
          <p className="section-subtitle">{t("methodology.sectionSubtitle")}</p>
        </div>

        <div className="methodology-grid">
          {methodology.map((m, i) => (
            <div key={i} className="technology-card animate-on-scroll" style={{ animationDelay: `${i * 0.1}s` }}>
              <div className="technology-card-icon">
                <m.Icon size={22} />
              </div>
              <h3 className="technology-card-title">{t(m.titleKey)}</h3>
              <p className="technology-card-desc">{t(m.descKey)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works animate-on-scroll">
        <div className="section-header">
          <span className="section-badge">{t("howItWorks.sectionBadge")}</span>
          <h2 className="section-title">{t("howItWorks.sectionTitle")}</h2>
        </div>

        <div className="steps-container">
          <div className="step">
            <div className="step-number">1</div>
            <h3>{t("howItWorks.step1.title")}</h3>
            <p>{t("howItWorks.step1.desc")}</p>
          </div>
          <div className="step-connector"></div>
          <div className="step">
            <div className="step-number">2</div>
            <h3>{t("howItWorks.step2.title")}</h3>
            <p>{t("howItWorks.step2.desc")}</p>
          </div>
          <div className="step-connector"></div>
          <div className="step">
            <div className="step-number">3</div>
            <h3>{t("howItWorks.step3.title")}</h3>
            <p>{t("howItWorks.step3.desc")}</p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section animate-on-scroll" id="contact">
        <div className="contact-card">
          <h2>{t("contact.title")}</h2>
          <p>
            {t("contact.subtitle")}
          </p>
          <div className="contact-emails">
            <div className="contact-email-item">
              <span className="contact-email-label">{t("contact.businessLabel")}</span>
              <a href="mailto:patryk.kotlowski@dialectoapp.com" className="contact-link">
                <IconBriefcase size={18} /> patryk.kotlowski@dialectoapp.com
              </a>
            </div>
            <div className="contact-email-item">
              <span className="contact-email-label">{t("contact.supportLabel")}</span>
              <a href="mailto:support@dialectoapp.com" className="contact-link">
                <IconMail size={18} /> support@dialectoapp.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="about-us-section animate-on-scroll" id="about">
        <div className="section-header">
          <span className="section-badge">{t("aboutUs.sectionBadge")}</span>
          <h2 className="section-title">{t("aboutUs.sectionTitle")}</h2>
          <p className="section-subtitle">{t("aboutUs.sectionSubtitle")}</p>
        </div>

        <div className="about-us-content">
          <div className="about-us-mission">
            <p>{t("aboutUs.mission")}</p>
          </div>

          <div className="about-us-team">
            <div className="team-member-card">
              <div className="team-member-avatar">
                <span className="team-member-initials">PK</span>
              </div>
              <div className="team-member-info">
                <h3 className="team-member-name">{t("aboutUs.team.member1.name")}</h3>
                <p className="team-member-role">{t("aboutUs.team.member1.role")}</p>
                <a
                  href={t("aboutUs.team.member1.linkedin")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="team-member-linkedin"
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  {t("aboutUs.viewLinkedIn")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section animate-on-scroll" id="faq">
        <div className="section-header">
          <span className="section-badge">{t("faq.sectionBadge")}</span>
          <h2 className="section-title">{t("faq.sectionTitle")}</h2>
          <p className="section-subtitle">{t("faq.sectionSubtitle")}</p>
        </div>

        <div className="faq-container">
          {[1, 2, 3, 4, 5].map((num) => (
            <FAQItem
              key={num}
              question={t(`faq.questions.q${num}.question`)}
              answer={t(`faq.questions.q${num}.answer`)}
            />
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta animate-on-scroll">
        <div className="final-cta-content">
          <h2>{t("finalCta.title")}</h2>
          <p>
            {t("finalCta.subtitle")}
          </p>
          <div className="final-cta-buttons">
            <a
              href="https://apps.apple.com/pl/app/dialecto/id6746904195"
              target="_blank"
              rel="noopener noreferrer"
              className="store-button apple on-dark"
              aria-label={t("hero.downloadAppStore")}
            >
              <AppStoreBadge />
              <span className="store-button-text">
                <span className="store-button-eyebrow">{t("hero.downloadOn")}</span>
                <span className="store-button-name">App Store</span>
              </span>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.dialectoapp&hl=en"
              target="_blank"
              rel="noopener noreferrer"
              className="store-button google on-dark"
              aria-label={t("hero.downloadPlayStore")}
            >
              <PlayStoreBadge />
              <span className="store-button-text">
                <span className="store-button-eyebrow">{t("hero.getItOn")}</span>
                <span className="store-button-name">Google Play</span>
              </span>
            </a>
          </div>
          <p className="cta-note">{t("finalCta.note")}</p>
        </div>
      </section>
    </div>
  );
}

// Feature Showcase — tabbed layout replacing 4 separate detail sections
function FeatureShowcase({ features, eyebrowLabel }) {
  const [active, setActive] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const switchFeature = useCallback((index) => {
    if (index === active || isAnimating) return;
    setIsAnimating(true);
    setTimeout(() => {
      setActive(index);
      setIsAnimating(false);
    }, 200);
  }, [active, isAnimating]);

  const f = features[active];

  return (
    <section className="feature-showcase" id="feature-showcase">
      <div className="showcase-inner">
        {/* Left: tabs + content */}
        <div className="showcase-content">
          <div className="showcase-tabs" role="tablist">
            {features.map((feat, i) => (
              <button
                key={feat.id}
                role="tab"
                aria-selected={i === active}
                className={`showcase-tab ${i === active ? "active" : ""}`}
                onClick={() => switchFeature(i)}
              >
                <span className="showcase-tab-icon">
                  <feat.Icon size={18} />
                </span>
                <span className="showcase-tab-label">{feat.title}</span>
              </button>
            ))}
          </div>

          <div className={`showcase-body ${isAnimating ? "fading" : ""}`} role="tabpanel">
            <h2 className="showcase-title">{f.title}</h2>
            <p className="showcase-desc">{f.fullDesc}</p>
            <div className="showcase-highlight">
              <span className="highlight-icon"><IconSparkles size={18} /></span>
              <p>{f.highlight}</p>
            </div>
          </div>
        </div>

        {/* Right: phone mockup */}
        <div className="showcase-visual">
          <div className="feature-phone-mockup">
            <div className="feature-phone-frame">
              <div className="phone-notch"></div>
              <img
                src={f.image}
                alt={f.imageAlt}
                className={`feature-phone-screen ${isAnimating ? "fading" : ""}`}
              />
            </div>
            <div className="feature-phone-glow"></div>
          </div>
        </div>
      </div>
    </section>
  );
}

// FAQ Item Component with accordion functionality
function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-item ${isOpen ? "open" : ""}`}>
      <button
        className="faq-question"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span>{question}</span>
        <span className="faq-icon" aria-hidden="true">
          <IconChevronDown size={20} />
        </span>
      </button>
      <div className="faq-answer">
        <p>{answer}</p>
      </div>
    </div>
  );
}

export default Home;
